import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Search, Bell, Menu } from 'lucide-react';
import { useAuth } from '@/auth/auth-context';
import { usePlatform } from '@/hooks/use-platform';

const pageTitles: Record<string, string> = {
  '/': 'Home',
  '/conversations': 'Reflections',
  '/tasks': 'Tasks',
  '/topics': 'Topics',
  '/assignees': 'Assignees',
  '/search': 'Search',
  '/profile': 'Profile',
  '/settings': 'Settings',
};

interface HeaderProps {
  onMenuClick?: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isMobile } = usePlatform();

  // Check if we're on a detail page (has path params)
  const isDetailPage = location.pathname.split('/').length > 2;

  // Get page title
  const baseTitle = pageTitles[location.pathname] || 'Find My Way';

  return (
    <header data-testid="app-header" className="sticky top-0 z-10 bg-white border-b border-stone-200">
      <div className="flex items-center justify-between h-16 px-4 lg:px-6">
        {/* Left section */}
        <div className="flex items-center gap-3">
          {isDetailPage ? (
            <button
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 rounded-lg text-stone-600 hover:bg-stone-100"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          ) : (
            <>
              {isMobile && onMenuClick ? (
                <button
                  data-testid="mobile-menu-button"
                  onClick={onMenuClick}
                  className="p-2 -ml-2 rounded-lg text-stone-600 hover:bg-stone-100 active:bg-stone-200"
                  aria-label="Open menu"
                >
                  <Menu className="w-6 h-6" />
                </button>
              ) : null}
              <h1 className="text-xl font-display font-semibold text-stone-900 lg:hidden">
                {baseTitle}
              </h1>
            </>
          )}
        </div>

        {/* Right section */}
        <div className="flex items-center gap-2">
          <button
            data-testid="search-button"
            onClick={() => navigate('/search')}
            className="p-2 rounded-lg text-stone-600 hover:bg-stone-100 lg:hidden"
          >
            <Search className="w-5 h-5" />
          </button>

          <button data-testid="notifications-button" className="p-2 rounded-lg text-stone-600 hover:bg-stone-100">
            <Bell className="w-5 h-5" />
          </button>

          {user && (
            <button
              data-testid="user-profile-button"
              onClick={() => navigate('/profile')}
              className="flex items-center gap-2 hover:bg-stone-100 rounded-full p-1 pr-3 transition-colors"
              title={user.email}
            >
              {user.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-8 h-8 rounded-full object-cover ring-2 ring-primary-200"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-medium">
                  {user.name.charAt(0).toUpperCase()}
                </div>
              )}
              <span className="text-sm font-medium text-stone-700 hidden sm:block max-w-[120px] truncate">
                {user.name.split(' ')[0]}
              </span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
